/**
 * Клиент для пакетной (chunked) загрузки файлов на сервер.
 * Использует jQuery $.ajax и нативный File.prototype.slice().
 *
 * Сервер должен принимать каждую часть отдельным POST-запросом с метаданными
 * (upload_id, chunk_index, total_chunks и т.д.) и склеивать файл после последней части.
 */
export class ChunkedFileUploader {

    /**
     * Возвращает набор опций по умолчанию.
     *
     * @returns {Object}
     */
    static getDefaultOptions() {
        return {
            /** URL endpoint для отправки частей */
            url: '/admin/chunked-upload',
            /** HTTP-метод запроса */
            method: 'POST',
            /** Размер одной части в байтах (по умолчанию 2 МБ) */
            chunkSize: 2 * 1024 * 1024,
            /** Сколько частей одного файла отправлять параллельно */
            parallelChunks: 1,
            /** Максимальное число повторных попыток при ошибке части */
            maxRetries: 3,
            /** Задержка между повторными попытками, мс */
            retryDelay: 1000,
            /** Дополнительные HTTP-заголовки */
            headers: {},
            /** Дополнительные поля FormData, добавляемые к каждому запросу */
            extraData: {},
            /** Имена полей FormData для метаданных и части файла */
            fields: {
                uploadId: 'upload_id',
                chunkIndex: 'chunk_index',
                totalChunks: 'total_chunks',
                fileName: 'file_name',
                fileSize: 'file_size',
                mimeType: 'mime_type',
                chunk: 'chunk',
            },
            /** CSRF-токен (если null — читается из meta[name="csrf-token"]) */
            csrfToken: null,
            /** Имя заголовка для CSRF-токена */
            csrfHeader: 'X-CSRF-TOKEN',
            /** Колбэк общего прогресса: ({ percent, loaded, total, file, fileIndex, fileCount }) */
            onProgress: null,
            /** Колбэк после успешной отправки части: ({ response, chunkIndex, totalChunks, file }) */
            onChunkSuccess: null,
            /** Колбэк после загрузки файла целиком: ({ response, file, fileIndex, fileCount }) */
            onFileSuccess: null,
            /** Колбэк после загрузки всех файлов: ({ responses, files }) */
            onSuccess: null,
            /** Колбэк при ошибке: ({ error, file, chunkIndex, xhr }) */
            onError: null,
        };
    }

    /**
     * @param {Object} [options] — параметры загрузки (см. getDefaultOptions)
     */
    constructor(options = {}) {
        this.setOptions(options);
        this._activeRequests = [];
        this._cancelled = false;
        this._progressState = {
            loaded: 0,
            total: 0,
            file: null,
            fileIndex: 0,
            fileCount: 0,
            completedBytes: 0,
        };
    }

    /**
     * Обновляет или переопределяет параметры загрузки.
     * Вложенные объекты fields, headers, extraData сливаются с текущими значениями.
     *
     * @param {Object} options
     * @returns {ChunkedFileUploader}
     */
    setOptions(options = {}) {
        const defaults = ChunkedFileUploader.getDefaultOptions();
        const current = this.options || defaults;

        this.options = {
            ...defaults,
            ...current,
            ...options,
            fields: { ...defaults.fields, ...current.fields, ...(options.fields || {}) },
            headers: { ...defaults.headers, ...current.headers, ...(options.headers || {}) },
            extraData: { ...defaults.extraData, ...current.extraData, ...(options.extraData || {}) },
        };

        return this;
    }

    /**
     * Загружает один или несколько файлов на сервер частями.
     *
     * @param {File|File[]|FileList} files
     * @returns {Promise<Array>} массив ответов сервера по каждому файлу
     */
    upload(files) {
        const fileList = this._normalizeFiles(files);

        if (!fileList.length) {
            return Promise.reject(new Error('No files to upload'));
        }

        this._cancelled = false;
        this._activeRequests = [];
        this._progressState.fileCount = fileList.length;
        this._progressState.total = fileList.reduce((sum, file) => sum + file.size, 0);
        this._progressState.loaded = 0;
        this._progressState.completedBytes = 0;

        return this._uploadFilesSequentially(fileList);
    }

    /**
     * Отменяет текущую загрузку и прерывает активные AJAX-запросы.
     */
    cancel() {
        this._cancelled = true;
        this._activeRequests.forEach((xhr) => {
            if (xhr && typeof xhr.abort === 'function') {
                xhr.abort();
            }
        });
        this._activeRequests = [];
    }

    /**
     * Проверяет, была ли загрузка отменена.
     *
     * @returns {boolean}
     */
    isCancelled() {
        return this._cancelled;
    }

    /**
     * Приводит входные данные к массиву File.
     *
     * @param {File|File[]|FileList} files
     * @returns {File[]}
     * @private
     */
    _normalizeFiles(files) {
        if (!files) {
            return [];
        }
        if (files instanceof File) {
            return [files];
        }
        if (files instanceof FileList) {
            return Array.from(files);
        }
        if (Array.isArray(files)) {
            return files.filter((file) => file instanceof File);
        }
        return [];
    }

    /**
     * Загружает файлы по одному, последовательно.
     *
     * @param {File[]} fileList
     * @returns {Promise<Array>}
     * @private
     */
    async _uploadFilesSequentially(fileList) {
        const responses = [];

        for (let fileIndex = 0; fileIndex < fileList.length; fileIndex++) {
            if (this._cancelled) {
                throw new Error('Upload cancelled');
            }

            const file = fileList[fileIndex];
            this._progressState.file = file;
            this._progressState.fileIndex = fileIndex;

            const response = await this._uploadFile(file);
            responses.push(response);

            if (typeof this.options.onFileSuccess === 'function') {
                this.options.onFileSuccess({
                    response,
                    file,
                    fileIndex,
                    fileCount: fileList.length,
                });
            }
        }

        if (typeof this.options.onSuccess === 'function') {
            this.options.onSuccess({ responses, files: fileList });
        }

        return responses;
    }

    /**
     * Загружает один файл, разбивая его на части.
     *
     * @param {File} file
     * @returns {Promise<*>} ответ сервера на последнюю часть
     * @private
     */
    async _uploadFile(file) {
        const uploadId = this._generateUploadId();
        const totalChunks = this._getTotalChunks(file.size);
        const chunkBytesCompleted = new Array(totalChunks).fill(0);
        const chunkResponses = new Array(totalChunks);
        let lastResponse = null;

        const uploadChunk = async (chunkIndex) => {
            if (this._cancelled) {
                throw new Error('Upload cancelled');
            }

            const { start, end } = this._getChunkRange(file.size, chunkIndex);
            const chunk = file.slice(start, end);

            const response = await this._sendChunkWithRetry(
                file,
                chunk,
                chunkIndex,
                totalChunks,
                uploadId,
                (loaded) => {
                    chunkBytesCompleted[chunkIndex] = loaded;
                    const fileLoaded = chunkBytesCompleted.reduce((sum, value) => sum + value, 0);
                    this._emitProgress(this._progressState.completedBytes + fileLoaded, file);
                }
            );

            chunkBytesCompleted[chunkIndex] = end - start;
            chunkResponses[chunkIndex] = response;

            if (typeof this.options.onChunkSuccess === 'function') {
                this.options.onChunkSuccess({
                    response,
                    chunkIndex,
                    totalChunks,
                    file,
                });
            }

            return response;
        };

        if (this.options.parallelChunks > 1) {
            await this._runWithConcurrency(
                Array.from({ length: totalChunks }, (_, i) => i),
                this.options.parallelChunks,
                uploadChunk
            );
            lastResponse = chunkResponses[totalChunks - 1];
        } else {
            for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
                lastResponse = await uploadChunk(chunkIndex);
            }
        }

        this._progressState.completedBytes += file.size;
        this._progressState.loaded = this._progressState.completedBytes;
        this._emitProgress(this._progressState.loaded, file);

        return lastResponse;
    }

    /**
     * Отправляет часть с повторными попытками при ошибке.
     *
     * @param {File} file
     * @param {Blob} chunk
     * @param {number} chunkIndex
     * @param {number} totalChunks
     * @param {string} uploadId
     * @param {Function} onChunkProgress
     * @returns {Promise<*>}
     * @private
     */
    async _sendChunkWithRetry(file, chunk, chunkIndex, totalChunks, uploadId, onChunkProgress) {
        let lastError = null;

        for (let attempt = 0; attempt <= this.options.maxRetries; attempt++) {
            if (this._cancelled) {
                throw new Error('Upload cancelled');
            }

            if (attempt > 0) {
                await this._delay(this.options.retryDelay);
            }

            try {
                return await this._sendChunk(
                    file,
                    chunk,
                    chunkIndex,
                    totalChunks,
                    uploadId,
                    onChunkProgress
                );
            } catch (error) {
                lastError = error;

                if (this._cancelled || error.statusText === 'abort') {
                    throw error;
                }
            }
        }

        if (typeof this.options.onError === 'function') {
            this.options.onError({
                error: lastError,
                file,
                chunkIndex,
                xhr: lastError?.xhr || null,
            });
        }

        throw lastError;
    }

    /**
     * Отправляет одну часть файла через $.ajax.
     *
     * @param {File} file
     * @param {Blob} chunk
     * @param {number} chunkIndex
     * @param {number} totalChunks
     * @param {string} uploadId
     * @param {Function} onChunkProgress
     * @returns {Promise<*>}
     * @private
     */
    _sendChunk(file, chunk, chunkIndex, totalChunks, uploadId, onChunkProgress) {
        const formData = this._buildChunkFormData(file, chunk, chunkIndex, totalChunks, uploadId);
        const headers = this._buildHeaders();

        return new Promise((resolve, reject) => {
            const jqXHR = $.ajax({
                url: this.options.url,
                method: this.options.method,
                data: formData,
                processData: false,
                contentType: false,
                headers,
                xhr: () => {
                    const xhr = $.ajaxSettings.xhr();
                    if (xhr.upload && typeof onChunkProgress === 'function') {
                        xhr.upload.addEventListener('progress', (event) => {
                            if (event.lengthComputable) {
                                onChunkProgress(event.loaded);
                            }
                        });
                    }
                    return xhr;
                },
                success: (response) => resolve(response),
                error: (xhr, status, errorMessage) => {
                    const error = new Error(errorMessage || 'Chunk upload failed');
                    error.xhr = xhr;
                    error.status = xhr?.status;
                    error.statusText = status;
                    error.response = xhr?.responseJSON ?? xhr?.responseText;
                    reject(error);
                },
            });

            this._activeRequests.push(jqXHR);

            jqXHR.always(() => {
                this._activeRequests = this._activeRequests.filter((request) => request !== jqXHR);
            });
        });
    }

    /**
     * Формирует FormData для отправки части файла.
     *
     * @param {File} file
     * @param {Blob} chunk
     * @param {number} chunkIndex
     * @param {number} totalChunks
     * @param {string} uploadId
     * @returns {FormData}
     * @private
     */
    _buildChunkFormData(file, chunk, chunkIndex, totalChunks, uploadId) {
        const { fields, extraData } = this.options;
        const formData = new FormData();

        formData.append(fields.uploadId, uploadId);
        formData.append(fields.chunkIndex, String(chunkIndex));
        formData.append(fields.totalChunks, String(totalChunks));
        formData.append(fields.fileName, file.name);
        formData.append(fields.fileSize, String(file.size));
        formData.append(fields.mimeType, file.type || 'application/octet-stream');
        formData.append(fields.chunk, chunk, file.name);

        Object.entries(extraData).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        });

        return formData;
    }

    /**
     * Собирает HTTP-заголовки, включая CSRF-токен Laravel.
     *
     * @returns {Object}
     * @private
     */
    _buildHeaders() {
        const headers = { ...this.options.headers };
        const csrfToken = this.options.csrfToken ?? this._getCsrfTokenFromMeta();

        if (csrfToken && this.options.csrfHeader) {
            headers[this.options.csrfHeader] = csrfToken;
        }

        return headers;
    }

    /**
     * Читает CSRF-токен из meta-тега страницы.
     *
     * @returns {string|null}
     * @private
     */
    _getCsrfTokenFromMeta() {
        const meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute('content') : null;
    }

    /**
     * Вычисляет количество частей для файла заданного размера.
     *
     * @param {number} fileSize
     * @returns {number}
     * @private
     */
    _getTotalChunks(fileSize) {
        return Math.max(1, Math.ceil(fileSize / this.options.chunkSize));
    }

    /**
     * Возвращает диапазон байтов для части с указанным индексом.
     *
     * @param {number} fileSize
     * @param {number} chunkIndex
     * @returns {{ start: number, end: number }}
     * @private
     */
    _getChunkRange(fileSize, chunkIndex) {
        const start = chunkIndex * this.options.chunkSize;
        const end = Math.min(start + this.options.chunkSize, fileSize);
        return { start, end };
    }

    /**
     * Генерирует уникальный идентификатор сессии загрузки.
     *
     * @returns {string}
     * @private
     */
    _generateUploadId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
            const random = Math.random() * 16 | 0;
            const value = char === 'x' ? random : (random & 0x3 | 0x8);
            return value.toString(16);
        });
    }

    /**
     * Вызывает колбэк onProgress с актуальными данными.
     *
     * @param {number} loaded
     * @param {File} file
     * @private
     */
    _emitProgress(loaded, file) {
        const { total, fileIndex, fileCount } = this._progressState;
        const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;

        if (typeof this.options.onProgress === 'function') {
            this.options.onProgress({
                percent,
                loaded,
                total,
                file,
                fileIndex,
                fileCount,
            });
        }
    }

    /**
     * Выполняет задачи с ограничением параллелизма.
     *
     * @param {Array} items
     * @param {number} concurrency
     * @param {Function} handler
     * @returns {Promise<Array>}
     * @private
     */
    async _runWithConcurrency(items, concurrency, handler) {
        const results = new Array(items.length);
        let currentIndex = 0;

        const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
            while (currentIndex < items.length) {
                if (this._cancelled) {
                    throw new Error('Upload cancelled');
                }

                const index = currentIndex++;
                results[index] = await handler(items[index]);
            }
        });

        await Promise.all(workers);
        return results;
    }

    /**
     * Пауза на указанное количество миллисекунд.
     *
     * @param {number} ms
     * @returns {Promise<void>}
     * @private
     */
    _delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
